import React, { useState } from "react";
import { useAtelierStore } from "@store/atelierStore";
import { WorkCharge, WorkshopSpecialty } from "../../types/garage/atelier";

const SPECIALTY_LABEL: Record<WorkshopSpecialty, string> = {
  mecanique: "Mécanique",
  carrosserie: "Carrosserie",
  peinture: "Peinture",
  diagnostique: "Diagnostique",
  autre: "Autre",
};

const SPECIALTY_COLORS: Record<WorkshopSpecialty, { bg: string; color: string }> = {
  mecanique: { bg: "#EEF0FF", color: "#4F46E5" },
  carrosserie: { bg: "#F3EEFF", color: "#7C3AED" },
  peinture: { bg: "#FEE2E2", color: "#DC2626" },
  diagnostique: { bg: "#D1FAE5", color: "#059669" },
  autre: { bg: "#F5F5F5", color: "#737373" },
};

function ChargeHeader({ charge, onBack, onClose }: { charge: WorkCharge; onBack?: () => void; onClose: () => void }) {
  const sc = SPECIALTY_COLORS[charge.specialty] ?? SPECIALTY_COLORS.autre;
  return (
    <div className="flex flex-row items-start justify-between" style={{ padding: "18px 20px 14px" }}>
      <div className="flex flex-col" style={{ gap: 4 }}>
        {onBack && (
          <button
            onClick={onBack}
            className="flex flex-row items-center text-h5 font-medium"
            style={{ color: "#A1A1A1", fontFamily: "Poppins", gap: 4, marginBottom: 2 }}
          >
            ← Retour
          </button>
        )}
        <span className="text-sm font-semibold" style={{ fontFamily: "Poppins", color: "#171717" }}>
          {charge.clientName}
        </span>
        <div className="flex flex-row items-center" style={{ gap: 6 }}>
          {charge.plate && (
            <>
              <span className="text-h5 font-medium" style={{ fontFamily: "Poppins", color: "#A1A1A1" }}>{charge.plate}</span>
              <span style={{ color: "#D9D9D9", fontSize: 10 }}>•</span>
            </>
          )}
          <span className="text-h5 font-medium" style={{ fontFamily: "Poppins", color: "#A1A1A1" }}>{charge.hours}h</span>
          <span style={{ color: "#D9D9D9", fontSize: 10 }}>•</span>
          <span
            className="text-h5 font-semibold"
            style={{ fontFamily: "Poppins", color: sc.color, background: sc.bg, padding: "1px 7px", borderRadius: 20 }}
          >
            {SPECIALTY_LABEL[charge.specialty]}
          </span>
        </div>
      </div>
      <button
        onClick={onClose}
        className="flex items-center justify-center"
        style={{ width: 28, height: 28, borderRadius: 8, background: "#F5F5F5", color: "#737373", fontSize: 16, flexShrink: 0 }}
      >
        ×
      </button>
    </div>
  );
}

export function ChargeModal({
  charge,
  mergeableCharges,
  onClose,
  onOpenOrdre,
}: {
  charge: WorkCharge;
  mergeableCharges: WorkCharge[];
  onClose: () => void;
  onOpenOrdre: () => void;
}) {
  const splitCharge = useAtelierStore((s) => s.splitCharge);
  const mergeCharges = useAtelierStore((s) => s.mergeCharges);

  const [view, setView] = useState<"actions" | "split" | "merge">("actions");
  const [splitValue, setSplitValue] = useState(Math.floor((charge.hours / 2) * 4) / 4);
  const [mergeTarget, setMergeTarget] = useState<WorkCharge | null>(null);

  const maxVal = +(charge.hours - 0.25).toFixed(2);
  const remainder = +(charge.hours - splitValue).toFixed(2);
  const splitRatio = splitValue / charge.hours;

  const actionBtn = {
    fontFamily: "Poppins",
    color: "#171717",
    background: "#FAFAFA",
    border: "1px solid #E5E5E5",
    borderRadius: 10,
    padding: "10px 14px",
    gap: 10,
    textAlign: "left" as const,
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: "rgba(0,0,0,0.3)" }}
      onClick={onClose}
    >
      <div
        className="flex flex-col bg-white rounded-2xl shadow-2xl"
        style={{ width: 340, overflow: "hidden" }}
        onClick={(e) => e.stopPropagation()}
      >
        <ChargeHeader
          charge={charge}
          onBack={view !== "actions" ? () => { setView("actions"); setMergeTarget(null); } : undefined}
          onClose={onClose}
        />

        <div style={{ height: 1, background: "#F0F0F0" }} />

        {view === "actions" && (
          <div className="flex flex-col" style={{ padding: "12px 12px 16px", gap: 6 }}>
            {charge.ordre_id && (
              <button onClick={() => { onOpenOrdre(); onClose(); }} className="flex flex-row items-center w-full text-h4 font-medium" style={actionBtn}>
                Voir l'ordre de réparation
              </button>
            )}
            {charge.hours > 0.5 && (
              <button onClick={() => setView("split")} className="flex flex-row items-center w-full text-h4 font-medium" style={actionBtn}>
                Découper la charge
              </button>
            )}
            {mergeableCharges.length > 0 && (
              <button onClick={() => setView("merge")} className="flex flex-row items-center w-full text-h4 font-medium" style={actionBtn}>
                Fusionner avec une autre charge
              </button>
            )}
          </div>
        )}

        {view === "split" && (
          <div className="flex flex-col" style={{ padding: "16px 20px 20px", gap: 16 }}>
            <div className="flex flex-col" style={{ gap: 8 }}>
              <div className="flex flex-row" style={{ height: 32, borderRadius: 8, gap: 3 }}>
                <div style={{ flex: splitRatio, background: "#EEF0FF", border: "1.5px solid #A5B4FC", borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center", transition: "flex 0.1s" }}>
                  <span className="text-h4 font-bold" style={{ fontFamily: "Poppins", color: "#4F46E5", whiteSpace: "nowrap" }}>{splitValue}h</span>
                </div>
                <div style={{ flex: 1 - splitRatio, background: "#F3EEFF", border: "1.5px solid #C4A8FF", borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center", transition: "flex 0.1s" }}>
                  <span className="text-h4 font-bold" style={{ fontFamily: "Poppins", color: "#7C3AED", whiteSpace: "nowrap" }}>{remainder}h</span>
                </div>
              </div>
              <input
                type="range"
                min={0.25}
                max={maxVal}
                step={0.25}
                value={splitValue}
                onChange={(e) => setSplitValue(parseFloat(e.target.value))}
                className="w-full"
                style={{ accentColor: "#6366F1" }}
              />
              <div className="flex flex-row justify-between">
                <span className="text-h5 font-medium" style={{ fontFamily: "Poppins", color: "#A1A1A1" }}>Partie 1</span>
                <span className="text-h5 font-medium" style={{ fontFamily: "Poppins", color: "#A1A1A1" }}>Partie 2</span>
              </div>
            </div>
            <div className="flex flex-row justify-end" style={{ gap: 8 }}>
              <button onClick={onClose} className="text-h4 font-medium" style={{ fontFamily: "Poppins", color: "#737373", padding: "7px 16px", borderRadius: 8, border: "1px solid #E5E5E5", background: "#FAFAFA" }}>
                Annuler
              </button>
              <button onClick={() => { splitCharge(charge.id, splitValue); onClose(); }} className="text-h4 font-medium" style={{ fontFamily: "Poppins", color: "#FFF", background: "#6366F1", padding: "7px 16px", borderRadius: 8 }}>
                Découper
              </button>
            </div>
          </div>
        )}

        {view === "merge" && (
          <div className="flex flex-col" style={{ padding: "12px 12px 16px", gap: 10 }}>
            {/* Charge list */}
            <div className="flex flex-col" style={{ gap: 4 }}>
              {mergeableCharges.map((c) => {
                const sc2 = SPECIALTY_COLORS[c.specialty] ?? SPECIALTY_COLORS.autre;
                const isSelected = mergeTarget?.id === c.id;
                return (
                  <button
                    key={c.id}
                    onClick={() => setMergeTarget(isSelected ? null : c)}
                    className="flex flex-row items-center justify-between w-full text-h4 font-medium"
                    style={{
                      fontFamily: "Poppins",
                      background: isSelected ? sc2.bg : "#FAFAFA",
                      border: `1.5px solid ${isSelected ? sc2.color : "#E5E5E5"}`,
                      borderRadius: 10,
                      padding: "10px 14px",
                      transition: "all 0.1s",
                    }}
                  >
                    <div className="flex flex-col items-start" style={{ gap: 1 }}>
                      <span style={{ color: isSelected ? sc2.color : "#171717" }}>{c.clientName}</span>
                      {c.plate && <span className="text-h5" style={{ color: "#A1A1A1" }}>{c.plate}</span>}
                    </div>
                    <span className="text-h4 font-semibold" style={{ color: isSelected ? sc2.color : "#A1A1A1" }}>{c.hours}h</span>
                  </button>
                );
              })}
            </div>

            {/* Result preview */}
            {mergeTarget && (
              <div
                className="flex flex-row items-center justify-center"
                style={{ gap: 8, padding: "10px 14px", background: "#F8F8FF", border: "1px solid #E0E3FF", borderRadius: 10 }}
              >
                <span className="text-h4 font-semibold" style={{ fontFamily: "Poppins", color: "#6366F1" }}>{charge.hours}h</span>
                <span className="text-h4" style={{ color: "#A1A1A1" }}>+</span>
                <span className="text-h4 font-semibold" style={{ fontFamily: "Poppins", color: "#6366F1" }}>{mergeTarget.hours}h</span>
                <span className="text-h4" style={{ color: "#A1A1A1" }}>=</span>
                <span className="text-h4 font-bold" style={{ fontFamily: "Poppins", color: "#4F46E5" }}>
                  {+(charge.hours + mergeTarget.hours).toFixed(2)}h
                </span>
              </div>
            )}

            <div className="flex flex-row justify-end" style={{ gap: 8 }}>
              <button onClick={onClose} className="text-h4 font-medium" style={{ fontFamily: "Poppins", color: "#737373", padding: "7px 16px", borderRadius: 8, border: "1px solid #E5E5E5", background: "#FAFAFA" }}>
                Annuler
              </button>
              <button
                onClick={() => { if (mergeTarget) { mergeCharges(charge.id, mergeTarget.id); onClose(); } }}
                disabled={!mergeTarget}
                className="text-h4 font-medium"
                style={{ fontFamily: "Poppins", color: "#FFF", background: mergeTarget ? "#6366F1" : "#D9D9D9", padding: "7px 16px", borderRadius: 8, transition: "background 0.1s" }}
              >
                Fusionner
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Keep old export name for backwards compat
export { ChargeModal as SplitModal };
